import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { Plus } from 'lucide-react'
import { buttonVariants } from '@/components/ui/button'
import ProtectedContent from 'app/frontend/base/components/ProtectedContent'
import Select from 'app/frontend/base/components/Select'
import NewsItem from './NewsItem'
import { NewsPermission } from './types'

const NewsIndex = ({
  newsItems,
  isPending,
  teamName,
  createPermission,
  editPermissionForItem,
}: {
  newsItems: wowaudit.NewsItem[] | undefined
  isPending: boolean
  teamName?: string
  createPermission: NewsPermission
  editPermissionForItem: (item: wowaudit.NewsItem) => NewsPermission
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const publishedItems = newsItems?.filter((item) => item.published) ?? []
  const unpublishedItems = newsItems?.filter((item) => !item.published) ?? []

  if (isPending) return null

  return (
    <div>
      <h2 className='flex items-center justify-between w-full gap-3 max-w-4xl'>
        <span>{t('shared.news')}</span>
        <ProtectedContent
          section={createPermission.section}
          requirement={createPermission.requirement}
          renderUnauthorized={false}
          requireAllTeams={createPermission.requireAllTeams}
        >
          <div className='flex items-center gap-2'>
            {unpublishedItems.length > 0 && (
              <Select
                placeholder={
                  <span className='text-foreground'>
                    {t('news.edit-drafts')}
                  </span>
                }
                value={undefined}
                setValue={(value) => {
                  if (value) {
                    navigate(`${value}/edit`)
                  }
                }}
                options={unpublishedItems.map((item) => ({
                  value: String(item.id),
                  label:
                    item.title?.slice(0, 30) +
                      (item.title && item.title.length > 30 ? '…' : '') ||
                    t('news.no-title'),
                  sortLabel: item.title || '',
                  className: !item.title ? 'italic text-muted-foreground' : '',
                  description: `${dayjs(item.updatedAt).format(
                    'MMM D, YYYY HH:mm',
                  )}${
                    item.createdBy?.obfuscatedBattletag
                      ? ` • ${item.createdBy.obfuscatedBattletag}`
                      : ''
                  }`,
                }))}
                className='w-28'
                optionWidth='fit'
              />
            )}
            <Link
              to='new'
              className={buttonVariants({ variant: 'default', size: 'sm' })}
            >
              <Plus className='w-4 h-4 mr-2' />
              {t('news.new-post')}
            </Link>
          </div>
        </ProtectedContent>
      </h2>

      {publishedItems.length === 0 ? (
        <p>{t('news.no-posts')}</p>
      ) : (
        <div className='flex flex-col gap-4 max-w-4xl'>
          {publishedItems.map((item) => (
            <NewsItem
              key={item.id}
              item={item}
              inList={true}
              teamName={teamName}
              editPermission={editPermissionForItem(item)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default NewsIndex
